import { useSelector } from 'react-redux';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { getClientAppServices } from 'src/common/services';
import { useStores } from 'src/common/stores';
import { selectIsAuthenticated } from '../selectors/auth';
import { getSelectChangedFavoritesForGuideId } from '../selectors/me';
import { getSelectProfileIsFollowing } from '../selectors/profiles';

/**
 * A util that returns the guideId follow state based on follow states that have changed
 * during the current session or falls back to the initial follow state of the guideItem
 * @param guideId - a guide id of the guide item
 * @param [fallbackIsFollowing] - optional fallback following state to use
 * @returns {boolean}
 */
export const useIsFollowingGuideId = (guideId, fallbackIsFollowing) => {
  const { playerStore } = useStores();
  const metadata = playerStore.metadata() || {};

  // store.me.changedFavorites.(guideId)
  const controlledIsFollowing = useSelector(getSelectChangedFavoritesForGuideId(guideId));
  // store.player.followOptions.(guideId).isFollowing
  const { isFollowing: nowPlayingIsFollowing } =
    metadata?.followOptions?.find(({ guideId: followGuideId }) => followGuideId === guideId) || {};
  // store.profiles.(guideId).actions.follow.isFollowing - this is initial data
  const profileIsFollowing = useSelector(getSelectProfileIsFollowing(guideId));

  if (!useSelector(selectIsAuthenticated)) {
    return false;
  }

  return !!(
    controlledIsFollowing ??
    nowPlayingIsFollowing ??
    profileIsFollowing ??
    fallbackIsFollowing
  );
};

export const isFollowingGuideId = (guideId, fallbackIsFollowing) => {
  const reduxState = storeSingleton.state;
  const { playerStore } = getClientAppServices().stores;
  const metadata = playerStore.getState().metadata || {};

  // store.me.changedFavorites.(guideId)
  const controlledIsFollowing = getSelectChangedFavoritesForGuideId(guideId)(reduxState);
  // store.player.followOptions.(guideId).isFollowing
  const { isFollowing: nowPlayingIsFollowing } =
    metadata?.followOptions?.find(({ guideId: followGuideId }) => followGuideId === guideId) || {};
  // store.profiles.(guideId).actions.follow.isFollowing - this is initial data
  const profileIsFollowing = getSelectProfileIsFollowing(guideId)(reduxState);

  if (!selectIsAuthenticated(reduxState)) {
    return false;
  }

  return !!(
    controlledIsFollowing ??
    nowPlayingIsFollowing ??
    profileIsFollowing ??
    fallbackIsFollowing
  );
};
